<template>
  <section id="partners" class="py-16 bg-white">
    <div class="container mx-auto px-4">
      <h2 class="text-2xl sm:text-3xl font-semibold text-center text-gray-900">
        Ces clubs nous font confiance
      </h2>
      <p class="mt-3 text-center text-gray-600">
        Découvrez nos clubs partenaires au Maroc
      </p>
      
      <!-- Carte interactive avec carrousel par-dessus -->
      <div class="mt-10 flex justify-center relative mb-32">
        <div 
          ref="mapContainer" 
          class="w-3/4 max-w-4xl h-[500px] rounded-lg shadow-lg border border-gray-200 bg-gray-100 flex items-center justify-center map-container"
        >
          <div v-if="!map" class="text-gray-500 flex flex-col items-center">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
            <span>Chargement de la carte...</span>
          </div>
        </div>
        
        <!-- Carrousel des clubs par-dessus la carte -->
        <div class="absolute -bottom-[30%] left-1/2 transform -translate-x-1/2 w-full max-w-lg px-4 z-10">
          <!-- Conteneur du carrousel -->
          <div class="overflow-hidden rounded-xl shadow-lg">
            <div 
              class="flex transition-transform duration-500 ease-in-out"
              :style="{ transform: `translateX(-${currentClubIndex * 100}%)` }"
            >
              <div 
                v-for="club in clubs" 
                :key="club.name"
                class="w-full flex-shrink-0 bg-white"
              >
                <!-- Image du club en haut -->
                <div class="h-48 overflow-hidden">
                  <img
                    :src="club.image"
                    :alt="`Logo ${club.name}`"
                    class="w-full h-full object-cover"
                  />
                </div>
                
                <!-- Informations du club en bas -->
                <div class="p-6 text-center">
                  <h3 class="text-2xl font-bold text-gray-900 mb-3">{{ club.name }}</h3>
                  <div class="flex items-center justify-center text-base text-gray-600">
                    <svg class="w-5 h-5 mr-2 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd"></path>
                    </svg>
                    <span class="font-medium">{{ club.location }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Boutons de navigation -->
          <button 
            @click="previousClub"
            class="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-4 bg-white rounded-full p-3 shadow-lg border border-gray-200 hover:bg-gray-50 transition-all duration-200 hover:scale-110"
          >
            <svg class="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
            </svg>
          </button>
          
          <button 
            @click="nextClub"
            class="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-4 bg-white rounded-full p-3 shadow-lg border border-gray-200 hover:bg-gray-50 transition-all duration-200 hover:scale-110"
          >
            <svg class="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
            </svg>
          </button>
        </div>
        
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

// Corriger les icônes de Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
})

// Configuration des clubs
const clubs = ref([
  {
    name: 'City Ball - Oasis Sports City',
    location: 'Casablanca, Maroc',
    coordinates: [33.548489056, -7.63725620099], // latitude, longitude
    image: "https://i.ibb.co/7x5hG1W2/Whats-App-Image-2025-09-15-at-11-50-57.jpg"
  },
  {
    name: 'Saad Land Club',
    location: 'Rabat, Maroc',
    coordinates: [33.8423858085, -6.87859771511], // latitude, longitude
    image: "https://lh3.googleusercontent.com/gps-cs-s/AC9h4noFCEt0YcNO9ssrzOXNaD6bKTkLoh3A8HuJbW5331ri1CrJAdJuoyUVhpjF01gU4JTs8pCoU8XjzEcU3xkubuWYHliPWHfJP4BieWpgWyZOPoV348JdcRDI3bn6ymitiovt5qRTj7gOOL7j=s1360-w1360-h1020-rw"
  },
  {
    name: 'Padel Spot',
    location: 'Fès, Maroc',
    coordinates: [34.0031111, -4.9994722], // latitude, longitude
    image: 'https://firebasestorage.googleapis.com/v0/b/padelgo-test.firebasestorage.app/o/clubs%2FCarroussel.png?alt=media&token=4b0bde09-49c1-4bf7-976f-8a7138ad56fe'
  },
  {
    name: 'Motiv\' Club',
    location: 'Meknès, Maroc',
    coordinates: [33.9014709133, -5.5019572391], // latitude, longitude
    image: 'https://i.ibb.co/cSZb2J2B/Carroussel.png'
  }
])

const mapContainer = ref<HTMLElement>()
let map: L.Map | null = null

// Variables pour le carrousel
const currentClubIndex = ref(0)
let markers: L.Marker[] = []

// Créer des icônes personnalisées pour les marqueurs
const createCustomIcon = (isActive = false) => {
  const backgroundColor = isActive ? '#EF4444' : '#1E2652'
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        background: ${backgroundColor};
        width: 40px;
        height: 40px;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        border: 3px solid white;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
      ">
        <div style="
          transform: rotate(45deg);
          color: white;
          font-weight: bold;
          font-size: 16px;
          text-shadow: 0 1px 2px rgba(0,0,0,0.5);
        ">🎾</div>
      </div>
    `,
    iconSize: [40, 40],
    iconAnchor: [20, 40],
    popupAnchor: [0, -40]
  })
}

onMounted(() => {
  console.log('🔥 COMPOSANT PARTNERS SECTION MONTÉ')
  console.log('🔥 mapContainer.value:', mapContainer.value)
  console.log('🔥 Leaflet disponible:', typeof L)
  
  // Attendre que le DOM soit complètement chargé
  setTimeout(() => {
    console.log('🔥 TIMEOUT EXÉCUTÉ')
    console.log('🔥 mapContainer.value après timeout:', mapContainer.value)
    
    if (mapContainer.value) {
      try {
        console.log('🔥 Initialisation de la carte...')
        
        // S'assurer que le conteneur a des dimensions
        mapContainer.value.style.height = '500px'
        mapContainer.value.style.width = '100%'
        
        // Calculer le centre géographique des clubs
        const latitudes = clubs.value.map(club => club.coordinates[0])
        const longitudes = clubs.value.map(club => club.coordinates[1])
        
        const centerLat = (Math.min(...latitudes) + Math.max(...latitudes)) / 2
        const centerLng = (Math.min(...longitudes) + Math.max(...longitudes)) / 2
        
        console.log('🔥 Centre calculé:', centerLat, centerLng)
        
        // Créer la carte centrée sur les clubs
        map = L.map(mapContainer.value, {
          zoomControl: false,
          scrollWheelZoom: false,
          doubleClickZoom: false,
          touchZoom: false,
          boxZoom: false,
          keyboard: false,
          dragging: true
        }).setView([centerLat, centerLng], 6)
        console.log('🔥 Carte créée:', map)
        
        // Ajouter les tuiles de la carte avec un style moderne
        L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
          attribution: '© OpenStreetMap contributors © CARTO',
          subdomains: 'abcd',
          maxZoom: 20
        }).addTo(map)
        console.log('🔥 Tuiles ajoutées')
        
        // Forcer le redimensionnement de la carte
        setTimeout(() => {
          if (map) {
            map.invalidateSize()
            console.log('🔥 Carte redimensionnée')
          }
        }, 100)
        
        
        // Ajouter les marqueurs pour chaque club
        markers = []
        clubs.value.forEach((club, index) => {
          console.log(`🔥 Ajout du marqueur ${index + 1}:`, club.name, club.coordinates)
          
          const isActive = index === currentClubIndex.value
          const customIcon = createCustomIcon(isActive)
          const marker = L.marker([club.coordinates[0], club.coordinates[1]], { 
            icon: customIcon 
          }).addTo(map!)
          markers.push(marker)
          
          // Au lieu d'un popup, afficher le club dans le carrousel
          marker.on('click', () => {
            currentClubIndex.value = index
            updateMarkers()
          })
        })
        
            // Ajuster la vue pour inclure tous les marqueurs
            if (markers.length > 0) {
              const group = L.featureGroup(markers)
              map.fitBounds(group.getBounds().pad(2))
              console.log('🔥 Vue ajustée pour inclure tous les clubs')
            }
        
        console.log('🔥 Carte initialisée avec succès')
      } catch (error) {
        console.error('🔥 Erreur lors de l\'initialisation de la carte:', error)
      }
    } else {
      console.error('🔥 mapContainer.value est null')
    }
  }, 1000) // Augmenté le timeout à 1 seconde
})

onUnmounted(() => {
  if (map) {
    map.remove()
  }
})

// Redimensionner la carte quand la fenêtre change de taille
const handleResize = () => {
  if (map) {
    setTimeout(() => {
      map!.invalidateSize()
    }, 100)
  }
}

onMounted(() => {
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})

// Fonction pour mettre à jour les marqueurs
const updateMarkers = () => {
  if (map && markers.length > 0) {
    markers.forEach((marker, index) => {
      const isActive = index === currentClubIndex.value
      const newIcon = createCustomIcon(isActive)
      marker.setIcon(newIcon)
    })
  }
}

// Fonctions du carrousel avec défilement infini
const nextClub = () => {
  currentClubIndex.value = (currentClubIndex.value + 1) % clubs.value.length
  updateMarkers()
}

const previousClub = () => {
  currentClubIndex.value = currentClubIndex.value === 0 ? clubs.value.length - 1 : currentClubIndex.value - 1
  updateMarkers()
}
</script>

<style scoped>
/* Styles pour la carte */
:deep(.leaflet-container) {
  height: 100%;
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  z-index: 1;
  position: relative;
}

/* Styles pour les popups personnalisés */
:deep(.custom-popup .leaflet-popup-content-wrapper) {
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(0, 0, 0, 0.05);
  padding: 0;
  margin: 0;
  z-index: 1000;
}

:deep(.leaflet-popup) {
  z-index: 1000;
}

:deep(.custom-popup .leaflet-popup-content) {
  margin: 0;
  padding: 0;
  line-height: 1.5;
}

:deep(.custom-popup .leaflet-popup-tip) {
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Styles pour les marqueurs personnalisés */
:deep(.custom-marker) {
  background: transparent !important;
  border: none !important;
}

/* Animation pour les marqueurs */
:deep(.custom-marker div) {
  transition: all 0.3s ease;
}

:deep(.custom-marker:hover div) {
  transform: rotate(-45deg) scale(1.1);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
}

/* Styles pour les contrôles de la carte */
:deep(.leaflet-control-zoom a) {
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

:deep(.leaflet-control-zoom a:hover) {
  background: #f8fafc;
  border-color: #3b82f6;
  color: #3b82f6;
}

/* Styles pour l'attribution */
:deep(.leaflet-control-attribution) {
  background: rgba(255, 255, 255, 0.9);
  border-radius: 4px;
  font-size: 11px;
  padding: 2px 6px;
}

/* Animation d'apparition pour la carte */
.map-container {
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>


